// src/components/Header.tsx
import React, { useState } from 'react';
import { User } from '../../../types';
import { signInWithGoogle, signInWithEmail, signOutUser } from '../../../services/firebase';
import Navigation from './Navigation';

interface HeaderProps {
  user: User | null;
  isLoading: boolean;
}

const Header: React.FC<HeaderProps> = ({ user, isLoading }) => {
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isGoogleLoginInProgress, setIsGoogleLoginInProgress] = useState(false);

  const isAdmin = user?.role === "Admin";
  const isManager = user?.role === "Manager";

  const handleGoogleLogin = async () => {
    try {
      setLoginError(null);
      setIsGoogleLoginInProgress(true);

      const loggedInUser = await signInWithGoogle();
      setIsGoogleLoginInProgress(false);

      if (loggedInUser && (loggedInUser.role === "Admin" || loggedInUser.role === "Manager")) {
        return; // success
      }

      setLoginError('You are authenticated but do not have access to the Admin Dashboard.');
    } catch (error: any) {
      setIsGoogleLoginInProgress(false);

      let errorMessage = 'Google login failed. Please try again.';
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login cancelled — you closed the window.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup blocked — allow popups for this site.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setLoginError(errorMessage);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setLoginError('Please enter both email and password');
      return;
    }

    try {
      setLoginError(null);
      const loggedInUser = await signInWithEmail(email, password);

      if (!(loggedInUser.role === "Admin" || loggedInUser.role === "Manager")) {
        setLoginError('Access denied: You are not authorized for this dashboard.');
        return;
      }

      setEmail('');
      setPassword('');
      setShowEmailLogin(false);
    } catch (error: any) {
      console.error('Email login failed:', error);

      const errorMessage = error.message || 'Email login failed. Try again.';
      if (errorMessage.includes('Unauthorized access')) {
        setLoginError('Access denied: Not authorized.');
      } else if (errorMessage.includes('auth/')) {
        setLoginError('Invalid email or password.');
      } else {
        setLoginError(errorMessage);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
      setShowEmailLogin(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleEmailLogin = () => {
    setShowEmailLogin(!showEmailLogin);
    setLoginError(null);
  };

  return (
    <header className="header">
      <div className="header-content">
        <h1 className="header-title">Fabrik Admin</h1>

        {/* Navigation for Admin & Manager */}
        {user && (isAdmin || isManager) && (
          <Navigation className="header-navigation" role={user.role ?? "User"} />
        )}

        <div className="auth-container">
          {isLoading ? (
            <span>Loading...</span>
          ) : isGoogleLoginInProgress ? (
            <span>Redirecting to Google...</span>
          ) : user ? (
            <div className="user-info">
              <span className="user-name">
                {user.displayName || user.email || 'User'} ({user.role})
              </span>
              <button className="logout-button" onClick={handleLogout}>
                Logout
              </button>
            </div>
          ) : (
            <div className="login-options">
              {loginError && <div className="login-error">{loginError}</div>}

              {showEmailLogin ? (
                <form className="email-login-form" onSubmit={handleEmailLogin}>
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="login-input"
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-input"
                  />
                  <div className="login-buttons">
                    <button type="submit" className="login-button">
                      Login
                    </button>
                    <button
                      type="button"
                      className="toggle-login-button"
                      onClick={toggleEmailLogin}
                    >
                      Back
                    </button>
                  </div>
                </form>
              ) : (
                <div className="login-buttons">
                  <button className="login-button google" onClick={handleGoogleLogin}>
                    Login with Google
                  </button>
                  <button className="login-button email" onClick={toggleEmailLogin}>
                    Login with Email
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
